import React from 'react';

const DEFAULT_CUE_MS = 600;
const DEFAULT_WINDOW_MS = 3000;

// The local letters must stay individually READABLE. If they blur into a
// texture the task silently becomes global-only: every local trial is a guess,
// and the switch cost — the entire measurement — collapses into noise. This
// floor is what keeps that from happening at the smallest supported width.
const LOCAL_MIN_PX = 16;

const MASKS = {
  H: [
    '10001',
    '10001',
    '11111',
    '10001',
    '10001',
  ],
  S: [
    '11111',
    '10000',
    '11111',
    '00001',
    '11111',
  ],
};

const CUE_LABELS = {
  global: 'Big letter',
  local: 'Small letters',
};

function CueView({ stimulus, compact = false }) {
  return (
    <div
      style={{
        minWidth: compact ? 76 : 160,
        padding: compact ? 'var(--space-2)' : 'var(--space-3) var(--space-4)',
        borderRadius: compact ? 'var(--radius-control)' : 'var(--radius-md)',
        border: '1px solid var(--border-strong)',
        background: 'var(--bg-elevated)',
        color: 'var(--text-primary)',
        fontSize: compact ? 'var(--text-label-size)' : 'var(--text-body-size)',
        fontWeight: 750,
        lineHeight: 1.2,
        letterSpacing: '0.01em',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {CUE_LABELS[stimulus?.cue] || 'Choose the named level'}
    </div>
  );
}

function StimulusView({ stimulus, compact = false }) {
  const globalLetter = stimulus?.globalLetter;
  const localLetter = stimulus?.localLetter;
  const mask = MASKS[globalLetter] || [];
  const cells = Array.from({ length: 25 }, (_, index) => {
    const row = Math.floor(index / 5);
    const col = index % 5;
    return mask[row]?.[col] === '1';
  });

  // ONE label naming BOTH levels, in a fixed order, never mentioning the cue.
  // A sighted participant sees both levels at once and has to apply the cue to
  // pick one; naming only the cued level here would hand a screen-reader user
  // the answer outright, and naming the wrong one would make the task
  // unanswerable. The cue itself is announced separately by CueView, which is
  // exactly the split the visual presentation has.
  return (
    <div
      role="img"
      aria-label={`Large ${globalLetter || 'unknown'} made of small ${localLetter || 'unknown'} letters`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1em)',
        gridTemplateRows: 'repeat(5, 1em)',
        gap: compact ? 1 : 'clamp(3px, 0.9vw, 7px)',
        // Cells are 1em squares, so this is the local letters' own size. The
        // floor is load-bearing, not cosmetic — see LOCAL_MIN_PX.
        fontSize: compact ? 8 : `clamp(${LOCAL_MIN_PX}px, 5vw, 36px)`,
        fontWeight: 750,
        lineHeight: 1,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        color: 'var(--text-primary)',
      }}
    >
      {cells.map((occupied, index) => (
        <span
          key={index}
          aria-hidden="true"
          data-local-letter={occupied ? localLetter : undefined}
          style={{
            width: '1em',
            height: '1em',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {occupied ? localLetter : ''}
        </span>
      ))}
    </div>
  );
}

export const navonSwitchTask = {
  id: 'navon-switch',
  name: 'Big and Small',
  pillar: 'flexibility',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'A big letter made from many small letters will appear.',
    'The cue tells you whether to report the big letter or the small letters.',
    'The two are never the same — read the cue every time.',
    'Press H for H and S for S.',
  ],
  // The mnemonic letter keys stay bound because the two answers ARE letters and
  // a participant reaches for them first. The home-row and arrow keys the rest
  // of the battery uses are bound alongside, in on-screen button order.
  targets: [
    { id: 'H', label: 'H', keys: ['h', 'H', 'f', 'ArrowLeft'], hint: 'H · F · ←' },
    { id: 'S', label: 'S', keys: ['s', 'S', 'j', 'ArrowRight'], hint: 'S · J · →' },
  ],
  examples: [
    {
      stimulus: { globalLetter: 'H', localLetter: 'S', cue: 'global' },
      response: 'H',
      note: 'report the big letter',
    },
    {
      stimulus: { globalLetter: 'S', localLetter: 'H', cue: 'local' },
      response: 'H',
      note: 'report the small letters',
    },
  ],
  toAnswer: (targetId) => ({ letter: targetId }),

  // The cue is shown ALONE for cue_stimulus_interval_ms, then the figure
  // appears and the clock starts AT THE FIGURE. Measuring from the cue would
  // fold the whole preparation interval into every reaction time and destroy
  // the flexibility cost, which is the entire measurement. The cue then stays
  // visible (cuePersists left at its default) because this is not a memory
  // task — the participant must never have to recall which level applies.
  //
  // Number() coercion is load-bearing: the runner's acceptance ceiling is
  // windowMs + 250, so a string window would concatenate instead of add
  // ('3000' + 250 === '3000250') and every late response would be accepted.
  // `||`, not `??`: a served 0 is a broken parameter, not an instant timeout.
  timing: (params) => ({
    fixationMs: 300,
    cueMs: Number(params?.cue_stimulus_interval_ms) || DEFAULT_CUE_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    // Deliberately short, and identical on every trial. Practice and scored
    // trials are ONE CONTINUOUS STREAM: the server defines each trial against
    // its PREDECESSOR, so the first scored trial (index 4) takes its
    // predecessor from the last practice trial (index 3). A pause, an
    // interstitial, or merely a longer gap at that boundary invalidates it.
    gapMs: 200,
  }),

  // PRACTICE ONLY, gated by the runner on trial.isPractice. Both levels are on
  // screen and applying the cue IS the task, so nothing withheld is computed
  // here. It reads only the stimulus it is handed: the runner also offers a
  // context carrying the previous trial's stimulus, and deriving anything from
  // that would classify this trial against its predecessor — which the client
  // is never told, by design, because the cost of a changed level is the thing
  // being measured.
  practiceExpectation: (stimulus) =>
    stimulus?.cue === 'global' ? stimulus.globalLetter : stimulus?.localLetter,
  CueView,
  StimulusView,
};
